import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import usePageParams from 'hooks/usePageParams';
import useQueryParams from 'hooks/useQueryParams';
import { Loader } from 'ncoded-component-library';
import { DataRoomShareableLink } from 'models/ShareableLink';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import CreateShareableLinkProvider from 'router/subrouters/DataRoom/providers/CreateShareableLink';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import EditShareableLinkProvider from 'router/subrouters/DataRoom/providers/EditShareableLink';
import useRefetch from 'hooks/useRefetch';
import { getHasElementsInArray, getSortValue, sentryError } from 'utils';
import ShareableLinkContent from '../../subrouters/ShareableLinks/pages/ShareableLinks/components/ShareableLinkContent';
import { SORTING_VALUES } from '../../subrouters/ShareableLinks/pages/ShareableLinks/components/ShareableLinksTable/components/data';
import { useLockScrollOnReroute } from 'hooks/useLockScrollOnReroute';
import useBusEvent from 'hooks/useBusEvent';
import { CallbackBusType, StandardEventTypes } from 'modules/bus';

import './ShareableLinksContentContainer.styles.scss';

const LIMIT = '10';

type ShareableLinksContentContainerProps = {
  className?: string;
  showVisits?: boolean;
  onlyActive?: boolean;
};

const ShareableLinksContentContainer: React.FC<
  ShareableLinksContentContainerProps
> = (props) => {
  const { children, className, showVisits = false, onlyActive = false } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [shareableLinks, setShareableLinks] = useState<DataRoomShareableLink[]>(
    [],
  );

  const {
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);

  const { refetch, refetchFlag } = useRefetch();

  const page = usePageParams();
  const { params, setQueryParam } = useQueryParams();

  const { sort } = params;

  useLockScrollOnReroute();

  const isEmpty = useMemo(
    () => !getHasElementsInArray(shareableLinks),
    [shareableLinks],
  );

  const classes = classNames('shareable-links-content-container', className);

  const updateShareableLink = useCallback(
    (shareableLink: DataRoomShareableLink) =>
      setShareableLinks((old) =>
        old.map((link) =>
          link?._id === shareableLink?._id ? shareableLink : link,
        ),
      ),
    [],
  );

  const updateShareableLinkFromEvent = useCallback(
    ({ payload }: CallbackBusType<DataRoomShareableLink>) => {
      if (!payload?._id) return;

      updateShareableLink(payload);
    },
    [updateShareableLink],
  );

  useBusEvent(
    StandardEventTypes.SHAREABLE_LINK_UPDATED,
    updateShareableLinkFromEvent,
  );

  useEffect(() => {
    if (!sort)
      setQueryParam(
        'sort',
        getSortValue(SORTING_VALUES.linkExpiresDate, false),
      );
  }, [sort, setQueryParam]);

  useEffect(() => {
    (async () => {
      if (!sort) return;

      setIsLoading(true);
      try {
        const {
          data: { items, totalPages: nextTotalPages },
        } = await dataRoomApi.shareableLinks.getShareableLinks(dataRoomId, {
          $page: page,
          $limit: LIMIT,
          $sort: sort,
          $relations: '["dataRoomNodes", "dataRoomNda"]',
          ...(onlyActive && { isActive: true }),
        });

        setShareableLinks(items);
        setTotalPages(nextTotalPages);
      } catch (error) {
        sentryError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dataRoomId, onlyActive, page, sort, refetchFlag]);

  if (!sort) return null;

  return (
    <div className={classes}>
      <CreateShareableLinkProvider onSuccessFullyCreated={refetch}>
        <EditShareableLinkProvider updateShareableLink={updateShareableLink}>
          {onlyActive && !isLoading && isEmpty ? (
            children
          ) : (
            <ShareableLinkContent
              isLoading={isLoading}
              isEmpty={isEmpty}
              totalPages={totalPages}
              shareableLinks={shareableLinks}
              showVisits={showVisits}
              updateShareableLink={updateShareableLink}
            />
          )}
        </EditShareableLinkProvider>
      </CreateShareableLinkProvider>

      {isLoading && !isEmpty && <Loader />}
    </div>
  );
};

export default ShareableLinksContentContainer;
